import { useEffect, useState } from 'react';
import { Download, Bell, Smartphone, X, Check } from 'lucide-react';
import {
  initInstallPrompt,
  canPromptInstall,
  promptInstall,
  isStandalone,
  pushSupported,
  notificationPermission,
  isPushSubscribed,
  enablePush,
  disablePush,
} from '@/lib/pwa';
import { cn } from '@/lib/cn';

/** Barra para instalar la PWA y activar notificaciones. */
export function InstallBanner() {
  const [installAvailable, setInstallAvailable] = useState(false);
  const [installed, setInstalled] = useState(isStandalone());
  const [pushOn, setPushOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem('mundial-install-dismissed') === '1',
  );
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    initInstallPrompt(setInstallAvailable);
    isPushSubscribed().then(setPushOn);
  }, []);

  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const installable = installAvailable || isIos;

  const onInstall = async () => {
    if (canPromptInstall()) {
      const ok = await promptInstall();
      if (ok) setInstalled(true);
    } else if (isIos) {
      setIosHint(true);
    }
  };

  const onTogglePush = async () => {
    setBusy(true);
    try {
      if (pushOn) {
        await disablePush();
        setPushOn(false);
      } else {
        const r = await enablePush();
        setPushOn(r.ok);
        if (!r.ok && r.reason === 'denied') {
          alert('Activa las notificaciones desde los ajustes del navegador para recibir avisos.');
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const perm = notificationPermission();
  const showInstall = !installed && !dismissed && installable;
  const showPush = pushSupported() && perm !== 'unsupported' && (installed || !showInstall);

  if (!showInstall && !showPush) return null;

  return (
    <div className="relative overflow-hidden rounded-xl border border-primary/20 bg-gradient-to-br from-primary-subtle to-white p-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary text-white shadow-sm">
          <Smartphone size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-font-primary">Lleva la quiniela en tu bolsillo</p>
          <p className="text-xs text-font-secondary">
            Instala la app y activa las notificaciones para no perderte ningún partido ni la
            actualización de tus puntos.
          </p>
        </div>
        <div className="flex w-full flex-shrink-0 items-center gap-2 sm:w-auto">
          {showInstall && (
            <button
              onClick={onInstall}
              className="flex h-9 flex-1 items-center justify-center gap-1.5 rounded-md bg-primary px-3 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-dark sm:flex-initial"
            >
              <Download size={14} /> Instalar
            </button>
          )}
          {showPush && (
            <button
              onClick={onTogglePush}
              disabled={busy}
              className={cn(
                'flex h-9 flex-1 items-center justify-center gap-1.5 rounded-md border px-3 text-sm font-semibold transition sm:flex-initial',
                pushOn
                  ? 'border-accent/40 bg-accent-light text-accent'
                  : 'border-border bg-white text-font-secondary hover:border-primary hover:text-primary',
              )}
            >
              {pushOn ? <Check size={14} /> : <Bell size={14} />}
              {pushOn ? 'Notificaciones activas' : 'Activar avisos'}
            </button>
          )}
        </div>
        {showInstall && (
          <button
            onClick={() => {
              setDismissed(true);
              localStorage.setItem('mundial-install-dismissed', '1');
            }}
            className="absolute right-2 top-2 rounded-full p-1 text-font-tertiary hover:bg-white hover:text-font-secondary"
            aria-label="Cerrar"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {iosHint && (
        <p className="mt-3 rounded-lg bg-white p-2.5 text-xs text-font-secondary">
          En iPhone/iPad: toca el botón <strong>Compartir</strong> y luego{' '}
          <strong>“Agregar a inicio”</strong> para instalar la app.
        </p>
      )}
    </div>
  );
}
